import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, MapPin, Briefcase, Mail, Phone } from 'lucide-react';

// Team Data
const teamMembers = [
  {
    id: '2',
    name: 'Haining WANG',
    role: 'Partner, Architect',
    location: 'PARIS',
    email: 'hwang.archi@gmail.com',
    phone: '06 49 92 46 86',
    image: '/assets/images/haining-wang.png'
  },
  {
    id: '3',
    name: 'Sarah Chen',
    role: 'Senior Designer',
    location: 'Paris',
    email: 'schen@gmail.com',
    phone: '09 81 11 60 33',
    image: '/assets/images/sarah-chen.png'
  },
  {
    id: '4',
    name: 'Thomas Mueller',
    role: 'Project Manager',
    location: 'Paris',
    email: 'tmueller@gmail.com',
    phone: '09 81 11 60 33',
    image: '/assets/images/thomas-mueller.png'
  }
];

const Teams: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  // Extract unique locations and roles for filters
  const locations = Array.from(new Set(teamMembers.map(m => m.location)));
  const roles = Array.from(new Set(teamMembers.map(m => m.role.split(',')[0].trim()))); // Simplified roles

  const filteredMembers = useMemo(() => {
    return teamMembers.filter(member => {
      const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            member.role.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesLocation = selectedLocation ? member.location === selectedLocation : true;
      const matchesRole = selectedRole ? member.role.includes(selectedRole) : true;
      return matchesSearch && matchesLocation && matchesRole;
    });
  }, [searchQuery, selectedLocation, selectedRole]);

  return (
    <div className="pt-32 pb-20 bg-white min-h-screen">
      {/* Hero Section */}
      <div className="container mx-auto px-4 md:px-8 mb-16 text-center">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-6xl font-light mb-6"
        >
          {t('teams.title')}
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-lg text-gray-500 max-w-2xl mx-auto font-light"
        >
          {t('teams.intro')}
        </motion.p>
      </div>

      {/* Search & Filter */}
      <div className="container mx-auto px-4 md:px-8 mb-16 sticky top-24 z-30 bg-white/95 backdrop-blur py-4 border-b border-gray-100">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input 
              type="text" 
              placeholder={t('teams.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-none focus:outline-none focus:border-black transition-colors bg-gray-50"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-4 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
            <select 
              value={selectedLocation} 
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-4 py-2 border border-gray-200 bg-white min-w-[140px] cursor-pointer hover:border-gray-400 transition-colors focus:outline-none"
            >
              <option value="">{t('teams.allLocations')}</option>
              {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>

            <select 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-4 py-2 border border-gray-200 bg-white min-w-[140px] cursor-pointer hover:border-gray-400 transition-colors focus:outline-none"
            >
              <option value="">{t('teams.allRoles')}</option>
              {roles.map(role => <option key={role} value={role}>{role}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Team Grid */}
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {filteredMembers.map((member, index) => (
            <motion.div 
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="group"
            >
              {/* Avatar */}
              <div className="aspect-square rounded-full overflow-hidden bg-gray-100 mb-6 relative">
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 grayscale group-hover:grayscale-0"
                />
              </div>

              {/* Info */}
              <div className="text-center md:text-left">
                <h3 className="text-xl font-medium mb-1">{member.name}</h3>
                <p className="text-sm text-gray-500 mb-2 uppercase tracking-wide">{member.role}</p>
                
                <div className="flex flex-col gap-1 text-sm text-gray-400">
                  <div className="flex items-center justify-center md:justify-start gap-2">
                    <MapPin size={14} />
                    <span>{member.location}</span>
                  </div>
                  <a href={`mailto:${member.email}`} className="flex items-center justify-center md:justify-start gap-2 hover:text-black transition-colors">
                    <Mail size={14} />
                    <span>{member.email}</span>
                  </a>
                  <a href={`tel:${member.phone}`} className="flex items-center justify-center md:justify-start gap-2 hover:text-black transition-colors">
                    <Phone size={14} />
                    <span>{member.phone}</span>
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredMembers.length === 0 && (
          <div className="text-center py-20 text-gray-400">
            {t('teams.noResults')}
          </div>
        )}
      </div>
    </div>
  );
};

export default Teams;
